import React from 'react';
import PropTypes from 'prop-types';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core';

import { Flex, SvgIcon, Text, ButtonFilled, Box } from 'Components/Base';
import { ClassroomDrawer } from 'Components/Classroom/Components';
import { get as getTheme } from 'Components/Base/constants';
import { useDisclosure } from 'Util/Hooks';
import { useGoToPurchase } from 'Domain/ExternalNavigation';

const NEXT_BUTTON = {
  PURCHASE_COURSE: 'PURCHASE_COURSE',
  DEFAULT: 'DEFAULT',
};

const FooterWrapper = styled(Flex)`
  position: fixed;
  bottom: 0;
  box-shadow: 0px 4px 24px rgba(17, 17, 26, 0.025), 0px 4px 16px rgba(17, 17, 26, 0.05);
  height: ${({ theme }) => theme.space?.CLASSROOM_FOOTER_HEIGHT ?? 60}px;
  flex: 0 0 auto;
  @media (min-width: ${getTheme('breakpoints.1')}) {
    position: sticky;
    z-index: 1;
  }
`;

const useDrawerStyles = makeStyles({
  paperAnchorBottom: {
    height: '80vh',
    overflowY: 'inherit',
    background: 'transparent',
  },
});

const IconWrapper = styled((props) => (
  <Flex
    justifyContent={'center'}
    alignItems={'center'}
    width={'32px'}
    height={'32px'}
    bg={'sidebarToggleButton'}
    p={2}
    {...props}
  />
))`
  && {
    border-radius: 100%;
    min-width: 32px;
    position: absolute;
    right: 28px;
    top: -16px;
    cursor: pointer;
    z-index: 5;
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.05);

    @media (min-width: ${getTheme('breakpoints.1')}) {
      display: none;
    }
  }
`;

const ToggleIcon = (props) => {
  const { colors } = useTheme();
  return (
    <SvgIcon
      icon={SvgIcon.ICON_LIST.ARROW_DOWN}
      size={24}
      color={colors.sidebarToggleIcon ?? 'white'}
      {...props}
    />
  );
};

const PrevButton = ({ onPrev }) => {
  const { t } = useTranslation('classroom');
  return (
    <Flex alignItems={'center'} onClick={onPrev} style={{ cursor: 'pointer' }}>
      <SvgIcon icon={SvgIcon.ICON_LIST.BACK} size={24} color={'primary'} />
      <Text mr={1}>{t('BUTTON.PREVIOUS')}</Text>
    </Flex>
  );
};

const NextButton = ({ variant, onNext }) => {
  const { t } = useTranslation('classroom');
  const { enablePurchaseUrl } = useGoToPurchase();

  switch (variant) {
    case NEXT_BUTTON.PURCHASE_COURSE:
      if (!enablePurchaseUrl) return null;
      return (
        <ButtonFilled variant={'small'} color={'sidebarPurchaseCourseButton'} onClick={onNext}>
          {t('BUTTON.PURCHASE_COURSE')}
        </ButtonFilled>
      );

    case NEXT_BUTTON.DEFAULT:
    default:
      return (
        <Flex alignItems={'center'} onClick={onNext} style={{ cursor: 'pointer' }}>
          <Text mr={1}>{t('BUTTON.NEXT')}</Text>
          <SvgIcon icon={SvgIcon.ICON_LIST.ARROW_RIGHT} size={24} color={'primary'} />
        </Flex>
      );
  }
};

const Footer = ({
  onPrev,
  onNext,
  isFirstSubsection,
  isLastSubsection,
  isClassroomTrialMode,
  isMobile,
  drawerProps,
  children,
  autoPlayStatusComp,
}) => {
  const [isOpen, onOpen, onClose, onToggle] = useDisclosure();

  const { t } = useTranslation('classroom');
  const drawerClasses = useDrawerStyles();

  const onDrawerOpen = () => {
    onOpen();
    if (drawerProps.onDrawerOpen != null) {
      drawerProps.onDrawerOpen();
    }
  };

  const onDrawerClose = () => {
    onClose();
    if (drawerProps.onDrawerClose != null) {
      drawerProps.onDrawerClose();
    }
  };

  const onDrawerToggle = () => {
    onToggle();
    if (drawerProps.onDrawerToggle != null) {
      drawerProps.onDrawerToggle();
    }
  };

  const childrenComp =
    typeof children === 'function'
      ? children(onDrawerToggle)
      : React.cloneElement(children, {
          onToggleDrawer: onDrawerToggle,
        });

  return (
    <FooterWrapper
      alignItems="center"
      bg={'white'}
      width={1}
      px={6}
      pt={6}
      pb={4}
      justifyContent={'space-between'}
    >
      {autoPlayStatusComp && autoPlayStatusComp}
      {!isMobile && (
        <>
          {!isFirstSubsection && !isClassroomTrialMode ? <PrevButton onPrev={onPrev} /> : <Box />}
        </>
      )}
      {isMobile && (
        <ClassroomDrawer
          drawerProps={{
            classes: drawerClasses,
            ...drawerProps,
            onDrawerOpen,
            onDrawerClose,
            onDrawerToggle,
          }}
          isOpen={isOpen}
          onToggleComponentClick={onOpen}
          toggleComponent={
            <Flex alignItems={'center'}>
              <SvgIcon icon={SvgIcon.ICON_LIST.CONTENT_LIST} size={24} color={'primary'} />
              <Text ml={1}>{t('COURSE_TOC_FOOTER.TITLE')}</Text>
            </Flex>
          }
        >
          <>
            <IconWrapper onClick={onClose}>
              <ToggleIcon />
            </IconWrapper>
            {childrenComp}
          </>
        </ClassroomDrawer>
      )}
      {isClassroomTrialMode && <NextButton variant={NEXT_BUTTON.PURCHASE_COURSE} onNext={onNext} />}

      {!isLastSubsection && !isClassroomTrialMode && (
        <NextButton variant={NEXT_BUTTON.DEFAULT} onNext={onNext} />
      )}
    </FooterWrapper>
  );
};

Footer.propTypes = {
  onPrev: PropTypes.func,
  onNext: PropTypes.func,
  isMobile: PropTypes.bool,
  isFirstSubsection: PropTypes.bool,
  isLastSubsection: PropTypes.bool,
  isClassroomTrialMode: PropTypes.bool,
  drawerProps: PropTypes.object,
  autoPlayStatusComp: PropTypes.node,
};

Footer.defaultProps = {
  onPrev: () => {},
  onNext: () => {},
  drawerProps: {
    onDrawerClose: () => {},
    onDrawerOpen: () => {},
    onDrawerToggle: () => {},
  },
};

export default Footer;
